import React, { useEffect, useState, useCallback, useRef } from "react";
import navBackImgSrc from "$/nav-back-w.svg";
import downloadImgSrc from "$/download-w.svg";
import deleteImgSrc from "$/trash-w.svg";
// import { LayoutHeader } from "@/Header";
// import { Toast } from "@/Toast";

type MediaType = "image" | "video";

interface ImageViewerProps {
  imageUrl: string;
  isVisible: boolean;
  onClose: () => void;
  onDownload?: () => void;
  onDelete?: () => void;
  // 여러 미디어 지원을 위한 새로운 props
  images?: string[];
  currentIndex?: number;
  onImageChange?: (index: number) => void;
  mediaType?: MediaType; // 이미지 또는 동영상 타입
  defaultMuted?: boolean;
  defaultMutedList?: boolean[];
}

export const ImageViewer = ({
  imageUrl,
  isVisible,
  onClose,
  onDownload,
  onDelete,
  images,
  currentIndex = 0,
  onImageChange,
  mediaType = "image",
  defaultMuted = false,
  defaultMutedList,
}: ImageViewerProps) => {
  const [showToast] = useState(false);
  const [toastMessage] = useState("");
  const videoRef = useRef<HTMLVideoElement>(null);

  // 현재 미디어 인덱스 관리
  const [currentImageIndex, setCurrentImageIndex] = useState(currentIndex);

  // 미디어 배열이 있으면 사용, 없으면 단일 미디어로 처리
  const mediaList = images || [imageUrl];
  const currentMedia = mediaList[currentImageIndex];
  const shouldStartMuted =
    mediaType === "video" &&
    (defaultMutedList?.[currentImageIndex] ?? defaultMuted);

  // 미디어 변경 함수
  const changeMedia = useCallback(
    (newIndex: number) => {
      if (newIndex >= 0 && newIndex < mediaList.length) {
        // 동영상 재생 중이면 정지
        if (videoRef.current) {
          videoRef.current.pause();
        }
        setCurrentImageIndex(newIndex);
        onImageChange?.(newIndex);
      }
    },
    [mediaList.length, onImageChange]
  );

  // 이전 미디어로 이동
  const goToPrevious = useCallback(() => {
    console.log("이전 버튼 클릭됨", currentImageIndex);
    changeMedia(currentImageIndex - 1);
  }, [currentImageIndex, changeMedia]);

  // 다음 미디어로 이동
  const goToNext = useCallback(() => {
    console.log("다음 버튼 클릭됨", currentImageIndex);
    changeMedia(currentImageIndex + 1);
  }, [currentImageIndex, changeMedia]);

  // 동영상 변경 시 재생을 멈추고 저장된 기본 음소거 상태로 초기화
  useEffect(() => {
    const video = videoRef.current;
    if (!video || mediaType !== "video") return;

    video.pause();
    video.defaultMuted = shouldStartMuted;
    video.muted = shouldStartMuted;
  }, [mediaType, currentMedia, shouldStartMuted, isVisible]);

  // 터치 이벤트 상태
  const [touchStart, setTouchStart] = useState<number | null>(null);
  const [touchEnd, setTouchEnd] = useState<number | null>(null);

  // 스와이프 감지
  const handleTouchStart = (e: React.TouchEvent) => {
    setTouchStart(e.targetTouches[0].clientX);
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const handleTouchEnd = () => {
    if (!touchStart || !touchEnd) return;

    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > 50;
    const isRightSwipe = distance < -50;

    if (isLeftSwipe) {
      goToNext();
    } else if (isRightSwipe) {
      goToPrevious();
    }

    setTouchStart(null);
    setTouchEnd(null);
  };

  // currentIndex prop이 변경되면 내부 상태도 업데이트
  useEffect(() => {
    setCurrentImageIndex(currentIndex);
  }, [currentIndex]);

  // // ESC 키로 닫기
  // useEffect(() => {
  //   const handleEscKey = (event: KeyboardEvent) => {
  //     if (event.key === "Escape") {
  //       onClose();
  //     }
  //   };

  //   if (isVisible) {
  //     document.addEventListener("keydown", handleEscKey);
  //     // 스크롤 방지
  //     document.body.style.overflow = "hidden";
  //   }

  //   return () => {
  //     document.removeEventListener("keydown", handleEscKey);
  //     document.body.style.overflow = "unset";
  //   };
  // }, [isVisible, onClose]);

  const handleDownload = async () => {
    if (onDownload) {
      // gallery.tsx에서 전달한 onDownload 함수 사용
      onDownload();
    } else {
      // onDownload가 없으면 직접 다운로드 처리
      try {
        const response = await fetch(currentMedia);
        const blob = await response.blob();
        const downloadUrl = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = downloadUrl;
        const extension = mediaType === "video" ? ".mp4" : ".jpg";
        a.download = `${mediaType === "video" ? "video" : "image"}-${currentImageIndex + 1}-${Date.now()}${extension}`;
        a.target = "_blank";
        a.rel = "noopener noreferrer";
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(downloadUrl);
      } catch (error) {
        console.error("다운로드 중 오류 발생:", error);
      }
    }
  };

  const handleDelete = () => {
    if (onDelete) {
      onDelete();
    }
  };

  if (!isVisible) return null;

  return (
    <div
      className="fixed inset-0 z-[9999] bg-black"
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        zIndex: 9999,
        WebkitOverflowScrolling: "touch",
        WebkitTransform: "translateZ(0)",
        transform: "translateZ(0)",
        // iOS에서 확실하게 최상단에 표시
        isolation: "isolate",
        contain: "layout style paint",
      }}
    >
      {/* 헤더 */}
      <header
        className={`flex absolute top-0 right-0 left-0 z-20 items-center p-3 h-14`}
        style={{
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          zIndex: 20,
          paddingTop: `calc(12px + env(safe-area-inset-top))`,
          WebkitTransform: "translateZ(0)",
          transform: "translateZ(0)",
          // 배경 추가로 확실하게 덮기
          backgroundColor: "rgba(0, 0, 0, 0.8)",
          backdropFilter: "blur(10px)",
          WebkitBackdropFilter: "blur(10px)",
        }}
      >
        <button onClick={onClose} className="-mr-6">
          <img src={navBackImgSrc} alt="뒤로가기 아이콘" />
        </button>
        <span className="absolute left-1/2 text-base font-bold text-white transform -translate-x-1/2">
          {mediaType === "video" ? "동영상" : "사진첩"}{" "}
          {mediaList.length > 1 &&
            `(${currentImageIndex + 1}/${mediaList.length})`}
        </span>
        <div className="flex gap-2 items-center ml-auto">
          <button
            onClick={handleDownload}
            className="p-2 text-white rounded-full transition-colors hover:bg-white hover:bg-opacity-20"
          >
            <img src={downloadImgSrc} alt="다운로드 아이콘" />
          </button>

          {onDelete && (
            <button
              onClick={handleDelete}
              className="p-2 text-white rounded-full transition-colors hover:bg-white hover:bg-opacity-20"
            >
              <img src={deleteImgSrc} alt="삭제 아이콘" />
            </button>
          )}
        </div>
      </header>
      {/* 미디어 (이미지/동영상) */}
      <div
        className="flex justify-center items-center h-[80vh] relative"
        style={{
          marginTop: `calc(3.5rem + 12px + env(safe-area-inset-top))`,
        }}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
      >
        {mediaType === "video" ? (
          <div className="relative w-full h-full flex justify-center items-center">
            <video
              ref={videoRef}
              src={currentMedia}
              className="object-contain max-w-full max-h-full"
              controls
              playsInline
              {...({ webkitPlaysInline: true } as Record<string, boolean>)}
              onClick={(e) => {
                e.stopPropagation();
              }}
            />
          </div>
        ) : (
          <img
            src={currentMedia}
            alt="Full size image"
            className="object-contain max-w-full max-h-full"
            onClick={(e) => e.stopPropagation()}
          />
        )}

        {/* 좌우 네비게이션 버튼 (여러 미디어가 있을 때만 표시) */}
        {mediaList.length > 1 && (
          <>
            {/* 이전 버튼 */}
            {currentImageIndex > 0 && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  goToPrevious();
                }}
                className="flex absolute left-4 top-1/2 z-20 justify-center items-center w-10 h-10 text-white bg-black bg-opacity-50 rounded-full transition-all transform -translate-y-1/2 hover:bg-opacity-70"
              >
                <svg
                  className="w-6 h-6"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M15 19l-7-7 7-7"
                  />
                </svg>
              </button>
            )}

            {/* 다음 버튼 */}
            {currentImageIndex < mediaList.length - 1 && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  goToNext();
                }}
                className="flex absolute right-4 top-1/2 z-20 justify-center items-center w-10 h-10 text-white bg-black bg-opacity-50 rounded-full transition-all transform -translate-y-1/2 hover:bg-opacity-70"
              >
                <svg
                  className="w-6 h-6"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 5l7 7-7 7"
                  />
                </svg>
              </button>
            )}
          </>
        )}
      </div>

      {/* 배경 클릭으로 닫기 - 제거됨 */}
      {/* <div
         className="absolute inset-0 z-0"
         onClick={(e) => {
           // 이미지나 버튼 클릭이 아닌 배경 클릭일 때만 닫기
           if (e.target === e.currentTarget) {
             onClose();
           }
         }}
       /> */}

      {/* 토스트 메시지 */}
      {showToast && (
        <div className="absolute bottom-20 left-1/2 z-20 transform -translate-x-1/2">
          <div className="px-4 py-2 text-sm text-white bg-black bg-opacity-70 rounded-lg">
            {toastMessage}
          </div>
        </div>
      )}
    </div>
  );
};
